import React, { useState } from 'react';

import AvatarWithText from 'components/AvatarWithText';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import LinkConstants from 'constants/LinkConstants';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import UserAvatar from 'users/components/UserAvatar';

import Field from 'components/Field';
import Label from 'components/Label';
import Radio from 'components/Radio';
import { regex } from 'lib/assertions';

import './ContactMergeDialog.scss';

const emailRegexp = new RegExp(regex.email);

export type ContactMergeDialogProps = {
  name: string;
  deviceUid: string;
  loading: boolean;
  isRostered: boolean;
  onMergeRequest: () => void;
  onRemoveRequest: () => void;
  onRequestHide: () => void;
  onRequestBack: () => void;
  owner: {
    avatar?: string;
    initials: string;
    display_name: string;
    groupNames: string;
  };
};

const ContactMergeDialog = ({
  name,
  deviceUid,
  loading,
  isRostered,
  onMergeRequest,
  onRemoveRequest,
  onRequestHide,
  onRequestBack,
  owner,
}: ContactMergeDialogProps): JSX.Element => {
  const [option, setOption] = useState<'remove' | 'merge'>('merge');

  const handleMergeRequest = () => {
    if (option === 'remove') {
      onRemoveRequest();
    } else {
      onMergeRequest();
    }
  };

  const isEmailDevice = emailRegexp.test(deviceUid);

  return (
    <Dialog
      title={isEmailDevice ? 'Email already in use' : 'Phone number already in use'}
      size="xsmall"
      onRequestHide={onRequestHide}
      className="ContactMergeDialog">
      <BoxBody>
        <Row size="xxsmall">
          <Text size="xsmall" align="left" wrap="force">
            {deviceUid} is part of an existing Remind account:
          </Text>
        </Row>
        <Row>
          <div className="ContactMergeDialog-user-box">
            <AvatarWithText
              avatarSize="large"
              avatar={
                <UserAvatar
                  user={{ initials: owner.initials, avatarUrl: owner.avatar }}
                  color="whiteout"
                />
              }
              title={owner.display_name}
              subtext={
                <Text className="ContactMergeDialog-user-box-class-list">
                  {owner.groupNames.length > 0 ? `Classes: ${owner.groupNames}` : 'No classes'}
                </Text>
              }
            />
          </div>
        </Row>

        <Row size="medium">
          {isRostered ? (
            <Text size="xsmall" align="left">
              If this is the same person as {name}, you can merge them below, otherwise <Link href={LinkConstants.CONTACT_HELP} target="_blank">contact support</Link>.
            </Text>
          ) : (
            <Field>
              <Label>Choose an option</Label>
              <Radio
                checked={option === 'remove'}
                label={`${isEmailDevice ? 'Remove email' : 'Remove number'} from ${owner.display_name} and add to ${name}`}
                onChange={() => setOption('remove')}
              />
              <Radio
                checked={option === 'merge'}
                label={
                  <div>
                    <Text>Merge the accounts. </Text>
                    <Link href={LinkConstants.CONTACT_INFO_HELP} target="_blank">Learn more</Link>
                  </div>
                }
                onChange={() => setOption('merge')}
              />
            </Field>
          )}
        </Row>

        <Row size="small">
          <Button
            onClick={handleMergeRequest}
            data-test="merge-button"
            eventElementName="ContactMergeDialog-submit"
            type="submit"
            level="secondary"
            loading={loading}
            full>
            {isRostered ? 'Merge accounts' : 'Continue'}
          </Button>
        </Row>

        <Link onClick={onRequestBack}>Go Back</Link>
      </BoxBody>
    </Dialog>
  );
};

export default ContactMergeDialog;
